import React, { Component } from 'react';
import { QueryRenderer } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';

import withStyles from '@mui/styles/withStyles';
import { Table, TableHead, TableCell, TableBody, TableRow, TableSortLabel, Typography } from '@mui/material';
import { Divider, Grid, Paper, TextField } from '@mui/material';
import { TablePagination } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { addDays, format, isValid, startOfDay } from 'date-fns';

import ServerError from '../ui/ServerError';
import LoadingSpinner from '../ui/LoadingSpinner';
import AppBindStyles from '../../AppBindStyles';

// import { green, red, amber } from '@mui/material/colors';

const abEnvironment = new AbEnvironment();

const ResellerApiLogTableQuery = graphql`
query ResellerApiLogTableQuery($accountId: UUID, $accountKeyId: UUID, $keyName: String, $keyValue: String, $startTs: Datetime, $endTs: Datetime, $requestMethod: String, $requestEndpoint: String, $sourceIp: String, $userAgent: String, $responseCode: [Int], $direction: Int, $first: Int, $offset: Int) {
  getresellerapikeylogs(accountId: $accountId, accountKeyId: $accountKeyId, keyName: $keyName, keyValue: $keyValue, startTs: $startTs, endTs: $endTs, requestMethod: $requestMethod, requestEndpoint: $requestEndpoint, sourceIp: $sourceIp, userAgent: $userAgent, responseCode: $responseCode, direction: $direction, first: $first, offset: $offset) {
    totalCount
    nodes {
      created
      aklId
      accountId
      accountKeyId
      keyName
      keyValue
      requestId
      requestEndpoint
      requestMethod
      requestBody
      sourceIp
      userAgent
      responseCode
      responseBody
    }
  }
}
`;

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
    //marginTop: 10,
    justifyContent: 'flex-end',
    backgroundColor: theme.palette.background.default,
  },
  flex: {
    flex: 1,
  },
  paper: {
    maxWidth: 1200,
    marginTop: '16px'
  },
  detailPaper: {
    padding: 16
  },
  detailRow: {
    height: 16
  },
  detailCell: {
    borderBottom: 'none'
  },
  radio: {
    marginTop: 16
  }
});

class ResellerApiLogTable extends Component {
  state = {
    accountKeyId: null,
    keyName: null,
    keyValue: null,
    startTs: null,
    queryStartTsUtc: null,
    endTs: null,
    queryEndTsUtc: null,
    requestMethod: null,
    requestEndpoint: null,
    sourceIp: null,
    userAgent: null,
    responseCode: null,
    orderDir: 0,
    page: 0,
    rowsPerPage: 10,
    selected: 0
  };

  handleChange = name => event => {
    this.setState({
      [name]: event.target.value,
      page: 0,
      selected: 0
    });
  };

  handleChangePage = (event, page) => {
    this.setState({page: page, selected: 0});
  }
  
  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: event.target.value, page: 0, selected: 0});
  }

  toggleOrder = () => {
    let newDir = 0;
    if (this.state.orderDir === 0) {
      newDir = 1;
    }
    this.setState({orderDir: newDir, page: 0, selected: 0});
  } 

  setFromDate(theDate) {
    let vdate = new Date(theDate); // Start of date local timezone.
    if (isValid(vdate)) {
      let searchTsUtc = startOfDay(vdate).toISOString();
      this.setState({ startTs: vdate, queryStartTsUtc: searchTsUtc, page : 0 });
    }
  }

  setToDate(theDate) {
    let vdate = new Date(theDate);
    if (isValid(vdate)) {
      let searchTsUtc = startOfDay(addDays(vdate, 1)).toISOString();
      this.setState({ endTs: vdate, queryEndTsUtc: searchTsUtc, page : 0 });
    }
  }

  render() {
    const { isAuthenticatedReseller, isAuthenticatedVendor, logError } = this.props.auth;
    const classes = this.props.classes;

    // const statusOk = green[300];
    // const statusSuspended = red[300];
    // const statusCaution = amber[400];

    // Determine the offset
    let offset = 0;
    if (this.state.page > 0)
    {
      offset = this.state.page * this.state.rowsPerPage
    }
    let first = this.state.rowsPerPage;
  
    let totalCount = 0;


    if (isAuthenticatedReseller() || isAuthenticatedVendor()) {

      let accountId = abEnvironment.getUserAttribute('a_id');

      let theDir = 'asc';
      if (this.state.orderDir === 0) {
        theDir = 'desc';
      }
  
      return (
        <Grid container spacing={2} direction="row" justifyContent="flex-start">
          <Grid item style={AppBindStyles.datePickerField}>
            <DatePicker 
              disableFuture
              label="From date"
              value={this.state.startTs}
              onChange={(newValue) => {
                this.setFromDate(newValue);
              }}
              renderInput={(params) => (
                <TextField 
                  {...params}
                  sx={{
                    svg: {paddingRight: '2px'}
                  }}
                />
              )}
            />
          </Grid>

          <Grid item style={AppBindStyles.datePickerField}>
            <DatePicker 
              disableFuture
              label="To date"
              value={this.state.endTs}
              onChange={(newValue) => {
                this.setToDate(newValue);
              }}
              renderInput={(params) => (
                <TextField 
                  {...params}
                  sx={{
                    svg: {paddingRight: '2px'}
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <QueryRenderer
              environment={abEnvironment.getEnvironment()}
              query={ResellerApiLogTableQuery}
              variables={{accountId: accountId,
                          accountKeyId: this.state.accountKeyId,
                          keyName: this.state.keyName,
                          keyValue: this.state.keyValue,
                          startTs: this.state.queryStartTsUtc,
                          endTs: this.state.queryEndTsUtc,
                          requestMethod: this.state.requestMethod,
                          requestEndpoint: this.state.requestEndpoint,
                          sourceIp: this.state.sourceIp,
                          userAgent: this.state.userAgent,
                          responseCode: this.state.responseCode,
                          direction: this.state.orderDir,
                          first: first,
                          offset: offset}}
              render={({error, props})=> {
                if (error) {
                  logError(error.message);
                  return (
                    <ServerError/>
                  );
                } else if (props) {
                  // Determine offset information
                  totalCount = props.getresellerapikeylogs.totalCount;
                  let loglines = props.getresellerapikeylogs.nodes;
                  let selectedId = null;
                  let selected = null;
                  let selectedDate = null;
                  let spacedResp = 'No response body';
                  let spacedReq = null;
                  if (this.state.selected < loglines.length) {
                    selected = loglines[this.state.selected];
                    // selectedId = selected.requestId;
                    selectedId = selected.aklId;
                    selectedDate = format(new Date(selected.created+'Z'), 'yyyy-MM-dd HH:mm:ss');

                    try {
                      let rbo = JSON.parse(selected.responseBody);
                      spacedResp = JSON.stringify(rbo, null, '  ');
                    } catch (err) {
                      spacedResp = selected.responseBody;
                    }

                    if (selected.requestBody != null) {
                      try {
                        let rro = JSON.parse(selected.requestBody);
                        spacedReq = JSON.stringify(rro, null, '  ');
                      } catch (err) {
                        spacedResp = selected.requestBody;
                      }
                    }
                  }

                  let theRow = -1;

                  return ( 
                    <Grid container spacing={0}>
                      <Grid item>
                        <Paper>
                          <Table>
                            <TableHead style={AppBindStyles.tableHead}>
                              <TableRow style={AppBindStyles.tableHeadRow}>
                                <TableCell>
                                  <TableSortLabel
                                    direction={theDir}
                                    onClick={() => this.toggleOrder()} >
                                    Date
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell>Response</TableCell>
                                <TableCell>Method</TableCell>
                                <TableCell>Endpoint</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {loglines.map(n => {
                                theRow++;
                                let theEndpoint = n.requestEndpoint;
                                let cindex = theEndpoint.indexOf('customers/');
                                if (cindex >= 2) {
                                  cindex = cindex + 9;
                                  theEndpoint = theEndpoint.substring(0,cindex);
                                }

                                let ctime = format(new Date(n.created+'Z'), 'yyyy-MM-dd HH:mm:ss');
                                let aRow = theRow;
                                if (n.aklId === selectedId) {
                                  return (
                                    <TableRow key={n.aklId} selected={true}>
                                      <TableCell align="left">{ctime}</TableCell>
                                      <TableCell align="left">{n.responseCode}</TableCell>
                                      <TableCell align="left">{n.requestMethod}</TableCell>
                                      <TableCell align="left">{theEndpoint}</TableCell>
                                    </TableRow>
                                  );
                                }
                                return (
                                  <TableRow key={n.aklId} onClick={() => this.setState({selected: aRow})}>
                                    <TableCell align="left">{ctime}</TableCell>
                                    <TableCell align="left">{n.responseCode}</TableCell>
                                    <TableCell align="left">{n.requestMethod}</TableCell>
                                    <TableCell align="left">{theEndpoint}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </Paper>
                        <div style={AppBindStyles.paginationTableFooter}>
                          <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={totalCount}
                            rowsPerPage={this.state.rowsPerPage}
                            page={this.state.page}
                            backIconButtonProps={{
                              'aria-label': 'Previous Page',
                            }}
                            nextIconButtonProps={{
                              'aria-label': 'Next Page',
                            }}
                            onPageChange={this.handleChangePage}
                            onRowsPerPageChange={this.handleChangeRowsPerPage}
                          />                        
                        </div>
                      </Grid>
                      {selected != null &&
                      <Grid item>
                        <Grid container spacing={0} direction='column'>
                          <Grid item>
                            <Paper className={classes.detailPaper} square="true">
                              <Typography align="left" variant="subtitle">{selected.requestMethod} {selected.requestEndpoint}</Typography>
                              <Table size="small" style={{marginTop: '8px'}}>
                                <TableBody>
                                  <TableRow>
                                    <TableCell style={{borderBottom: 'none'}}>Status</TableCell>
                                    <TableCell style={{borderBottom: 'none'}}>{selected.responseCode}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell style={{borderBottom: 'none'}}>Id</TableCell>
                                    <TableCell style={{borderBottom: 'none'}}>{selected.requestId}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell style={{borderBottom: 'none'}}>Time</TableCell>
                                    <TableCell style={{borderBottom: 'none'}}>{selectedDate}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell style={{borderBottom: 'none'}}>IP address</TableCell>
                                    <TableCell style={{borderBottom: 'none'}}>{selected.sourceIp}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell style={{borderBottom: 'none'}}>User agent</TableCell>
                                    <TableCell style={{borderBottom: 'none'}}>{selected.userAgent}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell style={{borderBottom: 'none'}}>Key name</TableCell>
                                    <TableCell style={{borderBottom: 'none'}}>{selected.keyName}</TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                              <Divider style={{marginTop: '8px', marginBottom: '16px'}}/>
                              <Typography align="left" variant="subtitle">Response body</Typography>
                              <Typography align="left" variant="body2" style={{marginTop: '8px', maxWidth: '600px'}}>{spacedResp}</Typography>

                              {spacedReq != null &&
                              <React.Fragment>
                                <Divider style={{marginTop: '8px', marginBottom: '16px'}}/>                              
                                <Typography align="left" variant="subtitle">Request body</Typography>
                                <Typography align="left" variant="body2" style={{marginTop: '8px', maxWidth: '600px'}}>{spacedReq}</Typography>
                              </React.Fragment>}
                            </Paper>
                          </Grid>
                        </Grid>
                      </Grid>}
                    </Grid>
                  )
                }
                return (
                  <div>
                    <Typography>Loading...</Typography>
                    <LoadingSpinner/>
                  </div>
                );
              }}
            />
          </Grid>
        </Grid>
      );
    } else {
      return (
        <Redirect to='/home' />
      );
    }
  }
}

ResellerApiLogTable.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(ResellerApiLogTable);
